import { useOrganisationBillingSummaryQuery } from "@/hooks/useOrganisationBillingSummaryQuery";
import { isApiErrorStatus } from "@/api/ApiError";
import { formatMinutesAsHMM } from "@/lib/utils";
import { PeriodTable } from "@/components/tpr/BillingSummary/BillingSummary";

interface OrgBillingViewProps {
  organisationId: string;
}

/**
 * Shows an organisation's billing summary: an org-wide totals header (last month + this month
 * total hours across all members) followed by a per-member breakdown, each rendered using the
 * same PeriodTable used on the member dashboard.
 *
 * Consumed by the owner screen (OrganisationView Billing tab) and the admin/PM org detail
 * drawer (OrganisationBookingsDrawer Billing section).
 */
export default function OrgBillingView({ organisationId }: OrgBillingViewProps) {
  const { data, isLoading, isError, error } = useOrganisationBillingSummaryQuery(organisationId);

  const isDeployGap = isError && isApiErrorStatus(error, 404);

  if (isLoading) {
    return (
      <p className="text-sm text-gray-600 p-4 text-center">Loading billing summary…</p>
    );
  }

  if (isDeployGap) {
    return (
      <p className="text-sm text-gray-600 p-4 text-center">
        Billing summary isn't available right now — an update may be in progress. Please try again in a few minutes.
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600 p-4 text-center">
        Couldn't load the billing summary. Please try again later.
      </p>
    );
  }

  if (!data) return null;

  const { perMember, totals } = data;

  return (
    <div>
      {/* Org-level totals header */}
      <div
        data-testid="org-billing-totals"
        className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex gap-8"
      >
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Month</p>
          <p className="text-2xl font-semibold tabular-nums">{formatMinutesAsHMM(totals.lastMonthMinutes)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">This Month</p>
          <p className="text-2xl font-semibold tabular-nums">{formatMinutesAsHMM(totals.thisMonthMinutes)}</p>
        </div>
      </div>

      {/* Per-member breakdown */}
      {perMember.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No billing data available for this organisation.</p>
      ) : (
        perMember.map(({ memberId, memberName, summary }) => (
          <div
            key={memberId}
            data-testid={`org-billing-member-${memberId}`}
            className="mb-8"
          >
            <h3 className="text-base font-semibold mb-3">{memberName}</h3>
            <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-2 border-solid">
              <PeriodTable caption="Last Month" period={summary.lastMonth} emptyMessage="No bookings last month." />
              <PeriodTable
                caption="This Month (so far)"
                period={summary.thisMonth}
                emptyMessage="No completed bookings this month yet."
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
